export interface IndustryCaseStudy {
  client: string;
  project: string;
  challenge: string;
  outcome: string;
  engineers: number;
  duration: string;
  imageQuery: string;
}

export interface TalentPool {
  totalAvailable: number;
  roles: { title: string; count: number }[];
  avgExperience: string;
  topSkills: string[];
}

export interface IndustryDetail {
  id: string;
  name: string;
  icon: string;
  shortDesc: string;
  longDesc: string;
  compliance: string[];
  keyTech: string[];
  caseStudy: IndustryCaseStudy;
  talentPool: TalentPool;
  stat: string;
  statLabel: string;
  accentColor: string;
  accentBg: string;
  accentBorder: string;
  accentText: string;
}

export const industriesDetail: IndustryDetail[] = [
  {
    id: 'healthcare',
    name: 'Healthcare & Pharma',
    icon: 'ri-hospital-line',
    shortDesc: 'HIPAA-compliant engineering for modern healthtech.',
    longDesc:
      'We place engineers who understand FHIR, HL7, and HIPAA compliance inside out. From EHR integrations to telemedicine platforms and clinical trial data pipelines, our talent has shipped code in regulated healthcare environments.',
    compliance: ['HIPAA', 'FDA 21 CFR Part 11', 'GDPR for Health Data'],
    keyTech: ['FHIR / HL7', 'Python', 'React Native', 'AWS HIPAA'],
    caseStudy: {
      client: 'MedSync Health',
      project: 'Telemedicine Platform Rebuild',
      challenge:
        'Legacy monolith could not scale beyond 5,000 concurrent video sessions. Compliance audits were failing due to poor audit-logging.',
      outcome:
        'Delivered a microservices architecture on AWS with auto-scaling video infrastructure. Passed HIPAA audit in 6 weeks. 99.99% uptime.',
      engineers: 8,
      duration: '4 months',
      imageQuery:
        'Modern telemedicine app interface mockup on a laptop screen, clean white and teal medical UI design, soft ambient lighting, professional product photography style, minimal background',
    },
    talentPool: {
      totalAvailable: 34,
      roles: [
        { title: 'Backend Engineers', count: 14 },
        { title: 'Full-Stack Devs', count: 12 },
        { title: 'DevOps / SRE', count: 5 },
        { title: 'Security Engineers', count: 3 },
      ],
      avgExperience: '6.2 yrs',
      topSkills: ['FHIR', 'Node.js', 'PostgreSQL', 'Kubernetes'],
    },
    stat: '34',
    statLabel: 'Engineers Available',
    accentColor: 'bg-emerald-500',
    accentBg: 'bg-emerald-50',
    accentBorder: 'border-emerald-200',
    accentText: 'text-emerald-700',
  },
  {
    id: 'banking',
    name: 'Banking & Financial Services',
    icon: 'ri-bank-line',
    shortDesc: 'Fintech-grade engineering for core banking and trading.',
    longDesc:
      'Our finance specialists build core banking systems, real-time payment gateways, regulatory reporting pipelines, and algo-trading infrastructure. They understand PCI-DSS, SOX, and the urgency of millisecond latency.',
    compliance: ['PCI-DSS', 'SOX', 'GDPR', 'ISO 27001'],
    keyTech: ['Java / Spring', 'Kafka', 'PostgreSQL', 'Redis'],
    caseStudy: {
      client: 'Vertex Capital',
      project: 'Real-Time Payment Gateway',
      challenge:
        'Existing batch-processing settlement took 24 hours. Fraud detection was reactive, not real-time.',
      outcome:
        'Built an event-driven payment platform processing 12,000 TPS. Fraud detection latency dropped from minutes to 200ms. Settlement now T+0.',
      engineers: 12,
      duration: '6 months',
      imageQuery:
        'Futuristic fintech dashboard with charts and payment flows, dark navy UI with amber accent charts, glowing data streams, professional digital product render',
    },
    talentPool: {
      totalAvailable: 48,
      roles: [
        { title: 'Java Engineers', count: 18 },
        { title: 'Data Engineers', count: 10 },
        { title: 'Security Engineers', count: 8 },
        { title: 'QA / Testers', count: 12 },
      ],
      avgExperience: '7.4 yrs',
      topSkills: ['Java', 'Kafka', 'SQL', 'AWS'],
    },
    stat: '48',
    statLabel: 'Engineers Available',
    accentColor: 'bg-amber-500',
    accentBg: 'bg-amber-50',
    accentBorder: 'border-amber-200',
    accentText: 'text-amber-700',
  },
  {
    id: 'retail',
    name: 'Retail & E-commerce',
    icon: 'ri-shopping-cart-line',
    shortDesc: 'Platform scaling and conversion optimization at scale.',
    longDesc:
      'From omnichannel inventory sync to high-conversion checkout flows and recommendation engines, our retail engineers know how to move product. Black-Friday-tested infrastructure is our baseline.',
    compliance: ['PCI-DSS', 'GDPR', 'CCPA'],
    keyTech: ['Next.js', 'Node.js', 'Elasticsearch', 'Stripe'],
    caseStudy: {
      client: 'LuxeMarket',
      project: 'Omnichannel Commerce Platform',
      challenge:
        'Mobile conversion rate was 1.2%. Inventory oversold across 3 warehouses daily. Site crashed during flash sales.',
      outcome:
        'Rebuilt checkout flow lifted conversion to 4.8%. Real-time inventory sync eliminated overselling. Flash-sale traffic handled 50x baseline load.',
      engineers: 10,
      duration: '5 months',
      imageQuery:
        'Sleek e-commerce website displayed on a large monitor, luxury product grid layout, warm neutral tones with gold accents, professional studio photography style, soft shadows',
    },
    talentPool: {
      totalAvailable: 42,
      roles: [
        { title: 'Frontend Devs', count: 16 },
        { title: 'Backend Engineers', count: 14 },
        { title: 'Mobile Devs', count: 7 },
        { title: 'Cloud Architects', count: 5 },
      ],
      avgExperience: '5.8 yrs',
      topSkills: ['React', 'Node.js', 'GraphQL', 'AWS'],
    },
    stat: '42',
    statLabel: 'Engineers Available',
    accentColor: 'bg-rose-500',
    accentBg: 'bg-rose-50',
    accentBorder: 'border-rose-200',
    accentText: 'text-rose-700',
  },
  {
    id: 'logistics',
    name: 'Logistics & Supply Chain',
    icon: 'ri-truck-line',
    shortDesc: 'Route optimization and warehouse automation systems.',
    longDesc:
      'We supply engineers who build fleet management platforms, warehouse WMS integrations, demand-forecasting models, and last-mile delivery optimization algorithms that actually save fuel.',
    compliance: ['ISO 27001', 'SOC 2', 'GDPR'],
    keyTech: ['Python', 'TensorFlow', 'React', 'Google Maps API'],
    caseStudy: {
      client: 'SwiftFreight',
      project: 'AI Route Optimization Engine',
      challenge:
        'Fleet of 800 trucks was operating at 62% capacity utilization. Driver idle time averaged 3.2 hours per day.',
      outcome:
        'ML-powered routing boosted utilization to 91%. Driver idle time fell to 0.7 hours. Fuel costs dropped 18% in the first quarter.',
      engineers: 6,
      duration: '3 months',
      imageQuery:
        'Logistics control center with map screens showing truck routes, modern dark dashboard UI with green route lines, industrial warehouse atmosphere, professional tech photography',
    },
    talentPool: {
      totalAvailable: 22,
      roles: [
        { title: 'Python / ML Engineers', count: 8 },
        { title: 'Backend Devs', count: 7 },
        { title: 'Mobile Engineers', count: 4 },
        { title: 'Data Analysts', count: 3 },
      ],
      avgExperience: '5.1 yrs',
      topSkills: ['Python', 'Django', 'PostgreSQL', 'Docker'],
    },
    stat: '22',
    statLabel: 'Engineers Available',
    accentColor: 'bg-yellow-500',
    accentBg: 'bg-yellow-50',
    accentBorder: 'border-yellow-200',
    accentText: 'text-yellow-700',
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: 'ri-tools-line',
    shortDesc: 'Industry 4.0, IoT telemetry, and predictive maintenance.',
    longDesc:
      'Our manufacturing specialists connect shop-floor PLCs to cloud dashboards, build predictive maintenance models, and design MES integrations that turn raw sensor data into actionable uptime.',
    compliance: ['ISO 27001', 'NIST CSF', 'IEC 62443'],
    keyTech: ['C++', 'MQTT', 'Grafana', 'Azure IoT'],
    caseStudy: {
      client: 'AeroForm Industries',
      project: 'Predictive Maintenance Platform',
      challenge:
        'Unplanned downtime cost $2.1M annually. Maintenance was calendar-based, not condition-based. No unified sensor data view.',
      outcome:
        'IoT platform unified 2,400 sensors. Predictive models cut unplanned downtime by 74%. Maintenance costs reduced by $840K in year one.',
      engineers: 7,
      duration: '5 months',
      imageQuery:
        'Industrial IoT dashboard showing factory machine telemetry, dark UI with orange warning indicators, manufacturing floor background blurred, professional product photography',
    },
    talentPool: {
      totalAvailable: 18,
      roles: [
        { title: 'Embedded Engineers', count: 6 },
        { title: 'Backend Devs', count: 5 },
        { title: 'Data Engineers', count: 4 },
        { title: 'DevOps', count: 3 },
      ],
      avgExperience: '7.8 yrs',
      topSkills: ['C++', 'Python', 'MQTT', 'Linux'],
    },
    stat: '18',
    statLabel: 'Engineers Available',
    accentColor: 'bg-orange-500',
    accentBg: 'bg-orange-50',
    accentBorder: 'border-orange-200',
    accentText: 'text-orange-700',
  },
  {
    id: 'edtech',
    name: 'EdTech',
    icon: 'ri-graduation-cap-line',
    shortDesc: 'Learning platforms, LMS integrations, and assessment tools.',
    longDesc:
      'We build the platforms that scale education — from K-12 learning management systems to corporate LMS integrations, adaptive assessment engines, and real-time classroom collaboration tools.',
    compliance: ['FERPA', 'COPPA', 'WCAG 2.1 AA', 'GDPR'],
    keyTech: ['React', 'Node.js', 'MongoDB', 'WebRTC'],
    caseStudy: {
      client: 'LearnSphere',
      project: 'Adaptive Learning Platform',
      challenge:
        'Course completion rate was 23%. Content was static, not personalized. No real-time progress tracking for instructors.',
      outcome:
        'Adaptive algorithm personalized learning paths per student. Completion rate jumped to 67%. Instructor dashboard reduced intervention time by 40%.',
      engineers: 9,
      duration: '4 months',
      imageQuery:
        'Modern e-learning platform interface on a tablet, colorful student progress cards, friendly educational UI with warm yellow accents, clean white background, product photography',
    },
    talentPool: {
      totalAvailable: 28,
      roles: [
        { title: 'Full-Stack Devs', count: 12 },
        { title: 'Frontend Devs', count: 8 },
        { title: 'Mobile Devs', count: 5 },
        { title: 'UX Engineers', count: 3 },
      ],
      avgExperience: '4.9 yrs',
      topSkills: ['React', 'Node.js', 'TypeScript', 'MongoDB'],
    },
    stat: '28',
    statLabel: 'Engineers Available',
    accentColor: 'bg-pink-500',
    accentBg: 'bg-pink-50',
    accentBorder: 'border-pink-200',
    accentText: 'text-pink-700',
  },
  {
    id: 'agritech',
    name: 'AgriTech',
    icon: 'ri-plant-line',
    shortDesc: 'Precision agriculture, drone data, and farm management SaaS.',
    longDesc:
      'Our AgriTech engineers build satellite-image analysis pipelines, IoT soil-monitoring dashboards, and farm-management SaaS that helps growers make data-driven decisions from seed to sale.',
    compliance: ['GDPR', 'ISO 27001', 'FDA for Agri Data'],
    keyTech: ['Python', 'QGIS', 'React', 'AWS Lambda'],
    caseStudy: {
      client: 'GreenYield Farms',
      project: 'Precision Irrigation System',
      challenge:
        'Water usage was 40% above optimal. Yield predictions were manual and inaccurate. No real-time soil-moisture visibility.',
      outcome:
        'IoT soil sensors + satellite imagery reduced water use by 32%. Yield prediction accuracy improved from 62% to 91%.',
      engineers: 5,
      duration: '3 months',
      imageQuery:
        'Agriculture technology dashboard showing crop health maps, green and earth-tone UI with satellite imagery widgets, clean modern design, professional tech photography on light background',
    },
    talentPool: {
      totalAvailable: 14,
      roles: [
        { title: 'Data / ML Engineers', count: 5 },
        { title: 'Backend Devs', count: 4 },
        { title: 'Mobile Engineers', count: 3 },
        { title: 'IoT Engineers', count: 2 },
      ],
      avgExperience: '5.5 yrs',
      topSkills: ['Python', 'React', 'AWS', 'TensorFlow'],
    },
    stat: '14',
    statLabel: 'Engineers Available',
    accentColor: 'bg-lime-500',
    accentBg: 'bg-lime-50',
    accentBorder: 'border-lime-200',
    accentText: 'text-lime-700',
  },
  {
    id: 'saas',
    name: 'SaaS & Product Companies',
    icon: 'ri-apps-line',
    shortDesc: 'Multi-tenant architecture, CI/CD, and product analytics.',
    longDesc:
      'This is our bread and butter. We place engineers who have built and scaled B2B SaaS products — multi-tenant architecture, feature flags, usage-based billing, and product-led growth analytics.',
    compliance: ['SOC 2', 'GDPR', 'CCPA', 'ISO 27001'],
    keyTech: ['React', 'Node.js', 'PostgreSQL', 'Terraform'],
    caseStudy: {
      client: 'FlowStack SaaS',
      project: 'Multi-Tenant Platform Rewrite',
      challenge:
        'Single-tenant architecture made onboarding new customers a 3-week manual process. Infrastructure costs scaled linearly with customers.',
      outcome:
        'Multi-tenant redesign cut onboarding to 15 minutes. Infrastructure costs per customer dropped 68%. NRR improved by 22 points.',
      engineers: 11,
      duration: '5 months',
      imageQuery:
        'SaaS product dashboard with user analytics charts, clean white UI with teal accent buttons, modern B2B software interface, professional product photography on minimal background',
    },
    talentPool: {
      totalAvailable: 86,
      roles: [
        { title: 'Full-Stack Devs', count: 32 },
        { title: 'Frontend Engineers', count: 22 },
        { title: 'Backend Engineers', count: 18 },
        { title: 'DevOps / SRE', count: 14 },
      ],
      avgExperience: '5.3 yrs',
      topSkills: ['React', 'Node.js', 'TypeScript', 'AWS'],
    },
    stat: '86',
    statLabel: 'Engineers Available',
    accentColor: 'bg-primary-500',
    accentBg: 'bg-primary-50',
    accentBorder: 'border-primary-200',
    accentText: 'text-primary-700',
  },
  {
    id: 'telecom',
    name: 'Telecom',
    icon: 'ri-smartphone-line',
    shortDesc: 'Network orchestration, BSS/OSS, and 5G edge apps.',
    longDesc:
      'Our telecom engineers work on network orchestration platforms, billing and subscriber management systems, and edge-computing applications for 5G networks. They understand telco-grade reliability.',
    compliance: ['3GPP', 'GDPR', 'ISO 27001', 'ETSI'],
    keyTech: ['Java', 'Kubernetes', 'gRPC', 'Apache Flink'],
    caseStudy: {
      client: 'NexaCom Networks',
      project: '5G Edge Service Orchestrator',
      challenge:
        'Legacy BSS could not support dynamic 5G network slicing. Service provisioning took 48 hours. Customer churn was 14%.',
      outcome:
        'Cloud-native BSS enabled real-time network slicing. Provisioning dropped to 90 seconds. Customer churn fell to 6%.',
      engineers: 9,
      duration: '6 months',
      imageQuery:
        '5G network visualization with glowing node connections, dark futuristic UI with cyan and white light trails, professional tech concept art style, high detail render',
    },
    talentPool: {
      totalAvailable: 26,
      roles: [
        { title: 'Java Engineers', count: 10 },
        { title: 'Cloud / K8s Engineers', count: 7 },
        { title: 'Backend Devs', count: 5 },
        { title: 'Network Engineers', count: 4 },
      ],
      avgExperience: '7.1 yrs',
      topSkills: ['Java', 'Kubernetes', 'Kafka', 'gRPC'],
    },
    stat: '26',
    statLabel: 'Engineers Available',
    accentColor: 'bg-red-500',
    accentBg: 'bg-red-50',
    accentBorder: 'border-red-200',
    accentText: 'text-red-700',
  },
  {
    id: 'insurance',
    name: 'Insurance',
    icon: 'ri-shield-check-line',
    shortDesc: 'Policy automation, claims processing, and actuarial platforms.',
    longDesc:
      'We place engineers who streamline policy lifecycle management, automate claims adjudication with ML, and build actuarial modeling platforms that turn complex risk data into competitive pricing.',
    compliance: ['GDPR', 'SOX', 'PCI-DSS', 'State Regulations'],
    keyTech: ['Python', 'Django', 'React', 'Snowflake'],
    caseStudy: {
      client: 'ShieldSure Insurance',
      project: 'Claims Automation Platform',
      challenge:
        'Claims processing averaged 14 days. Fraud detection was manual and missed 30% of cases. Customer satisfaction was at 62 NPS.',
      outcome:
        'ML-powered claims auto-adjudication reduced average processing to 18 hours. Fraud detection accuracy hit 94%. NPS rose to 78.',
      engineers: 7,
      duration: '4 months',
      imageQuery:
        'Insurance claims dashboard with document scanning AI visualization, clean white and navy UI with green status indicators, professional fintech product photography, soft lighting',
    },
    talentPool: {
      totalAvailable: 20,
      roles: [
        { title: 'Python / ML Engineers', count: 7 },
        { title: 'Full-Stack Devs', count: 6 },
        { title: 'Data Engineers', count: 4 },
        { title: 'QA Engineers', count: 3 },
      ],
      avgExperience: '6.4 yrs',
      topSkills: ['Python', 'Django', 'React', 'PostgreSQL'],
    },
    stat: '20',
    statLabel: 'Engineers Available',
    accentColor: 'bg-teal-500',
    accentBg: 'bg-teal-50',
    accentBorder: 'border-teal-200',
    accentText: 'text-teal-700',
  },
];

export const featuredCaseStudies = industriesDetail.slice(0, 4).map((ind) => ({
  industryId: ind.id,
  industryName: ind.name,
  industryIcon: ind.icon,
  client: ind.caseStudy.client,
  project: ind.caseStudy.project,
  outcome: ind.caseStudy.outcome,
  engineers: ind.caseStudy.engineers,
  duration: ind.caseStudy.duration,
  imageQuery: ind.caseStudy.imageQuery,
  accentColor: ind.accentColor,
  accentBg: ind.accentBg,
  accentText: ind.accentText,
}));